import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { MfaChallenge } from "./MfaChallenge";

/**
 * Shows the code screen only for users who have turned on two-step
 * verification and haven't entered a code this session. Everyone else
 * passes straight through. Fails open if the check itself errors.
 */
export function MfaGate({ children }: { children: React.ReactNode }) {
  const { session, signOut } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState<"checking" | "ok" | "challenge">("checking");

  useEffect(() => {
    let cancelled = false;
    if (!session) { setState("ok"); return; }
    supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) return setState("ok");
      setState(data.currentLevel === "aal1" && data.nextLevel === "aal2" ? "challenge" : "ok");
    });
    return () => { cancelled = true; };
  }, [session?.access_token]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (state === "challenge") {
    return (
      <MfaChallenge
        onVerified={() => setState("ok")}
        onSignOut={async () => { queryClient.clear(); await signOut(); }}
      />
    );
  }
  return <>{children}</>;
}
